/**
 * EVO — xarid, import va sozlama ekranlarining URL holati.
 *
 * `lib/ops-search.ts` va `lib/report-search.ts` bilan bir xil qoidalar:
 *   • URL ISHONCHSIZ KIRISH — har maydon tozalanadi (`?page=-5`,
 *     `?status=DROP TABLE` ekranni buzmaydi, default'ga tushadi);
 *   • DEFAULT qiymatlar URL ga YOZILMAYDI — manzil qisqa va ulashsa bo'ladigan;
 *   • oq ro'yxatlar serverda takrorlanmaydi (`sortBy` tekshirilmaydi: noto'g'ri
 *     maydon 400 qaytaradi va jadval xato holatini ko'rsatadi).
 *
 * Alohida fayl: bu ekranlar `ops-search.ts` dagi savdo/ombor ekranlaridan
 * mustaqil va ularning importlarini (`lib/sales.ts`, `lib/inventory.ts`)
 * ortiqcha tortib kelmaydi.
 */
import type { AuditAction } from './audit-log';
import { AUDIT_ACTIONS, isAuditAction } from './audit-log';
import type { SortDir } from './products';
import type { BatchStatus, ImportEntityType, RowStatus } from './import-1c';
import {
  BATCH_STATUS_VALUES,
  IMPORT_ENTITY_TYPES,
  ROW_STATUS_VALUES,
} from './import-1c';
import type { LcStatus, PoStatus, ShipmentStatus } from './procurement';
import {
  LC_STATUS_VALUES,
  PO_STATUS_VALUES,
  SHIPMENT_STATUS_VALUES,
} from './procurement';
import type { ReorderRunStatus } from './replenishment';
import { REORDER_RUN_STATUSES } from './replenishment';
import { SUPPLIER_KINDS, type SupplierKind } from './suppliers';

function positiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Ro'yxatdagi qiymat yoki `undefined` — begona matn jimgina tashlanadi. */
function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** Sahifa, saralash va qidiruv — barcha ro'yxat ekranlarida bir xil. */
interface CommonListSearch {
  readonly page?: number;
  readonly sortBy?: string;
  readonly sortDir?: SortDir;
  readonly q?: string;
}

function commonList(search: Record<string, unknown>): CommonListSearch {
  const page = positiveInt(search.page);
  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.sortBy) ? { sortBy: search.sortBy as string } : {}),
    ...(search.sortDir === 'desc' ? { sortDir: 'desc' as SortDir } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
  };
}

// ---------------------------------------------------------------------------
// Xarid buyurtmalari
// ---------------------------------------------------------------------------

export interface PurchaseOrderSearch extends CommonListSearch {
  readonly status?: PoStatus;
  readonly supplierId?: number;
  readonly includeCancelled?: boolean;
}

export function parsePurchaseOrderSearch(
  search: Record<string, unknown>,
): PurchaseOrderSearch {
  const status = oneOf(search.status, PO_STATUS_VALUES);
  const supplierId = positiveInt(search.supplierId);

  return {
    ...commonList(search),
    ...(status ? { status } : {}),
    ...(supplierId !== undefined ? { supplierId } : {}),
    ...(search.includeCancelled === true || search.includeCancelled === 'true'
      ? { includeCancelled: true }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Ta'minotchilar
// ---------------------------------------------------------------------------

/**
 * Ta'minotchilar ro'yxati va kartochkasi.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN BU YERDA, `list-search.ts` DA EMAS                            │
 * │                                                                          │
 * │ Ekran menyuda «Ma'lumotnomalar» da turadi, lekin uning MA'LUMOT qatlami  │
 * │ xarid domenida: `lib/procurement.ts` da `SupplierRow` va tanlagich       │
 * │ allaqachon bor, `country`/`isActive` filtrlari esa `SupplierListQuery`   │
 * │ dan keladi. Umumiy `parseListSearch` bu ikki maydonni bilmaydi va uni    │
 * │ har ekran uchun kengaytirish — «umumiy» funksiyani asta-sekin hamma      │
 * │ ekranning yig'indisiga aylantirish demak.                                │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * `id` — ochilgan kartochka. Alohida marshrut (`/suppliers/$id`) qilinmadi:
 * ta'minotchilar soni o'nlab (yuzlab emas) va ro'yxatdan kartochkaga o'tib
 * qaytish filtrni yo'qotardi. Manzil esa baribir ulashiladigan bo'lib qoladi.
 */
export interface SupplierSearch extends CommonListSearch {
  /** ISO 3166-1 alpha-2, HAR DOIM bosh harfda — server ham shunday saqlaydi. */
  readonly country?: string;
  /**
   * Kontragent turi — 0054: zavod / logistika / xizmat.
   *
   * Manzilda saqlanadi, chunki «logistika kontragentlari» ro'yxati
   * ulashiladigan holat: moliyachi uni yozib qo'yadi va har ertalab
   * o'sha manzildan ochadi.
   */
  readonly kind?: SupplierKind;
  /** `undefined` — hammasi; `false` — faqat arxivlanganlar. */
  readonly isActive?: boolean;
  readonly id?: number;
}

const COUNTRY_CODE = /^[A-Za-z]{2}$/;

export function parseSupplierSearch(search: Record<string, unknown>): SupplierSearch {
  const raw = text(search.country);
  const country = raw !== undefined && COUNTRY_CODE.test(raw) ? raw.toUpperCase() : undefined;
  const id = positiveInt(search.id);

  // `oneOf` — manzildagi qiymat ISHONCHSIZ: `?kind=logistic` (harf yetishmaydi)
  // serverga borsa 400 qaytarardi va jadval «xatolik» holatiga tushardi.
  const kind = oneOf(search.kind, SUPPLIER_KINDS);

  return {
    ...commonList(search),
    ...(country !== undefined ? { country } : {}),
    ...(kind !== undefined ? { kind } : {}),
    // Uch holat: `true`, `false` va «filtrsiz». Shuning uchun `flag()` emas —
    // `?isActive=false` MA'NOLI so'rov («arxivlanganlarni ko'rsat»).
    ...(search.isActive === 'true' || search.isActive === true ? { isActive: true } : {}),
    ...(search.isActive === 'false' || search.isActive === false ? { isActive: false } : {}),
    ...(id !== undefined ? { id } : {}),
  };
}

// ---------------------------------------------------------------------------
// Avtomatik qayta zakaz — tahlillar ro'yxati
// ---------------------------------------------------------------------------

export interface ReorderRunSearch extends CommonListSearch {
  readonly status?: ReorderRunStatus;
  readonly categoryId?: number;
}

export function parseReorderRunSearch(search: Record<string, unknown>): ReorderRunSearch {
  const status = oneOf(search.status, REORDER_RUN_STATUSES);
  const categoryId = positiveInt(search.categoryId);

  return {
    ...commonList(search),
    ...(status ? { status } : {}),
    ...(categoryId !== undefined ? { categoryId } : {}),
  };
}

// ---------------------------------------------------------------------------
// Konteynerlar
// ---------------------------------------------------------------------------

export interface ShipmentSearch extends CommonListSearch {
  readonly status?: ShipmentStatus;
  readonly lcStatus?: LcStatus;
  readonly includeClosed?: boolean;
}

export function parseShipmentSearch(search: Record<string, unknown>): ShipmentSearch {
  const status = oneOf(search.status, SHIPMENT_STATUS_VALUES);
  const lcStatus = oneOf(search.lcStatus, LC_STATUS_VALUES);

  return {
    ...commonList(search),
    ...(status ? { status } : {}),
    ...(lcStatus ? { lcStatus } : {}),
    ...(search.includeClosed === true || search.includeClosed === 'true'
      ? { includeClosed: true }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Tovar qabullari
// ---------------------------------------------------------------------------

export type ReceiptStatus = 'draft' | 'posted' | 'cancelled';
const RECEIPT_STATUS_VALUES: readonly ReceiptStatus[] = ['draft', 'posted', 'cancelled'];

export interface GoodsReceiptSearch extends CommonListSearch {
  readonly status?: ReceiptStatus;
  /** Konteynerdan «Qabul qilish» tugmasi shu parametr bilan ochadi. */
  readonly shipmentId?: number;
  /**
   * Ochilgan qabul kartochkasi.
   *
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ `shipmentId` DAN FARQI — BIRI FILTR, IKKINCHISI KARTOCHKA            │
   * │                                                                      │
   * │ `shipmentId` RO'YXATNI toraytiradi va serverga uzatiladi. `id` esa   │
   * │ ro'yxatga umuman tegmaydi: u pastdagi kartochkani ochadi va          │
   * │ `GET /goods-receipts/:id` ga aylanadi.                               │
   * │                                                                      │
   * │ Oldin bu holat `useState` da edi va oqibati ikkita: qidiruvdan       │
   * │ («GR-2026-…») kelgan odam ro'yxatga tushardi, ochilgan kartochka esa │
   * │ sahifa yangilanganda yo'qolardi. Ikkalasi ham bitta sabab — manzil   │
   * │ hujjatni AYTMASDI.                                                   │
   * └──────────────────────────────────────────────────────────────────────┘
   */
  readonly id?: number;
}

export function parseGoodsReceiptSearch(
  search: Record<string, unknown>,
): GoodsReceiptSearch {
  const status = oneOf(search.status, RECEIPT_STATUS_VALUES);
  const shipmentId = positiveInt(search.shipmentId);
  const id = positiveInt(search.id);

  return {
    ...commonList(search),
    ...(status ? { status } : {}),
    ...(shipmentId !== undefined ? { shipmentId } : {}),
    ...(id !== undefined ? { id } : {}),
  };
}

// ---------------------------------------------------------------------------
// Import ustasi (wizard)
// ---------------------------------------------------------------------------

export interface ImportSearch {
  /**
   * Ochilgan partiya.
   *
   * URL da turishi MAJBURIY: import 4 bosqichli va operator o'rtada sahifani
   * yangilashi mumkin (yoki brauzer o'zi qayta yuklaydi). Partiya ID si
   * yo'qolsa, yuklangan fayl bazada qolib ketadi va uni ekrandan davom
   * ettirib bo'lmaydi.
   */
  readonly batchId?: number;
  readonly entityType?: ImportEntityType;
  readonly rowStatus?: RowStatus;
  readonly batchStatus?: BatchStatus;
}

export function parseImportSearch(search: Record<string, unknown>): ImportSearch {
  const batchId = positiveInt(search.batchId);
  const entityType = oneOf(search.entityType, IMPORT_ENTITY_TYPES);
  const rowStatus = oneOf(search.rowStatus, ROW_STATUS_VALUES);
  const batchStatus = oneOf(search.batchStatus, BATCH_STATUS_VALUES);

  return {
    ...(batchId !== undefined ? { batchId } : {}),
    ...(entityType ? { entityType } : {}),
    ...(rowStatus ? { rowStatus } : {}),
    ...(batchStatus ? { batchStatus } : {}),
  };
}

// ---------------------------------------------------------------------------
// «Xavfsizlik» bo'limi — hodisalar jurnali
// ---------------------------------------------------------------------------

/*
 * Ekran endi Sozlamalar ostida EMAS, o'z bo'limida (`/security`). Parser esa
 * shu yerda qoldi: bu fayl «xarid + import + ma'muriy ekranlarning URL
 * holati» va u ekranning MENYUDAGI o'rniga emas, `parseListSearch` dan
 * mustaqilligiga qarab bo'lingan (fayl boshidagi izoh).
 */
export interface AuditSearch {
  readonly entityTable?: string;
  readonly entityId?: number;
  readonly actorId?: number;
  readonly limit?: number;
  /**
   * Tanlangan amallar — VERGUL bilan ajratilgan KANONIK satr
   * (`update,delete`). Ro'yxat sifatida o'qish: `parseAuditActions()`.
   *
   * ╔════════════════════════════════════════════════════════════════════════╗
   * ║ NIMA UCHUN SATR, MASSIV EMAS                                           ║
   * ║                                                                        ║
   * ║ Massiv ham ishlardi — TanStack Router uni JSON qilib yozadi va         ║
   * ║ qaytib o'qiy oladi. Lekin manzil shunday ko'rinardi:                   ║
   * ║                                                                        ║
   * ║   /security?action=%5B%22update%22%2C%22delete%22%5D                   ║
   * ║                                                                        ║
   * ║ (jonli o'lchangan, taxmin emas). Bu fayl boshidagi qoida esa aniq:     ║
   * ║ «manzil qisqa va ULASHSA BO'LADIGAN» bo'lishi kerak. Xavfsizlik        ║
   * ║ bo'limida bu shunchaki did emas — bu yerdagi manzil aynan ULASHISH     ║
   * ║ uchun ochiladi: «mana bu havolaga qara, kim o'chirganini ko'rasan».    ║
   * ║ Vergulli shakl esa hujjatga ham, xatga ham qo'lda yozsa bo'ladigan.    ║
   * ║                                                                        ║
   * ║ Server IKKALA shaklni ham qabul qiladi                                 ║
   * ║ (`admin.controller.ts#AuditActionFilter`), ya'ni tanlov faqat manzil   ║
   * ║ o'qilishiga bog'liq.                                                   ║
   * ╚════════════════════════════════════════════════════════════════════════╝
   *
   * ┌────────────────────────────────────────────────────────────────────────┐
   * │ NIMA UCHUN OQ RO'YXAT BU YERDA HAM BOR                                 │
   * │                                                                        │
   * │ Odatda bu faylda serverdagi tekshiruv takrorlanmaydi (`sortBy`         │
   * │ tekshirilmaydi — noto'g'ri maydon 400 beradi va jadval xatoni          │
   * │ ko'rsatadi). Bu yerda esa boshqacha: `?action=deleted` (bitta ortiqcha │
   * │ harf) serverdan 400 oldirardi va butun EKRAN xato holatiga tushardi —  │
   * │ ya'ni qo'lda tahrirlangan manzil jurnalni umuman ochilmaydigan qilardi.│
   * │                                                                        │
   * │ Begona qiymat JIMGINA tashlanadi, qolgani ishlaydi. Hech biri qolmasa  │
   * │ filtr umuman qo'yilmaydi — «hech narsa topilmadi» emas, «hammasi».     │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  readonly action?: string;
  /**
   * Davr — SERVERGA uzatiladi (`GET /api/admin/audit?from=&to=`), `to` kunning
   * oxirigacha inklyuziv.
   *
   * Format bu yerda tekshiriladi (`YYYY-MM-DD`), chunki URL ishonchsiz kirish:
   * `?from=oops` serverdan 400 oldirardi va ekran «xatolik» ko'rsatardi,
   * aslida sabab qo'lda tahrirlangan manzil.
   */
  readonly from?: string;
  readonly to?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Jadval nomi — serverdagi naqshning aynan o'zi
 * (`admin.controller.ts#AuditQuerySchema`: `^[a-z][a-z0-9_]*$`).
 *
 * Bu tekshiruv ma'lumotni CHEKLAMAYDI (bazadagi 68 qiymatning hammasi shu
 * naqshda), lekin `?entityTable=DROP TABLE` ni serverga yetkazmaydi — u
 * yerdan 400 kelardi va butun ekran xato holatiga tushardi.
 */
const TABLE_NAME = /^[a-z][a-z0-9_]*$/;

/**
 * `?action=update,delete` yoki `?action=update&action=delete` — IKKALASI ham.
 *
 * Router bir kalitning ikki qiymatini MASSIV qilib beradi, qo'lda yozilgan
 * URL esa odatda vergul bilan bo'ladi. Bittasini rad etish narxi bor:
 * noto'g'ri shakl JIMGINA «filtrsiz» ga aylanardi va butun jurnal qaytardi —
 * ya'ni filtr ishlayotgandek ko'rinib, aslida ishlamasdi.
 *
 * Natija HAR DOIM kanonik massiv: takrorsiz va `AUDIT_ACTIONS` tartibida.
 * Tartib muhim, chunki undan URL yasaladi: bir xil tanlov har doim bir xil
 * manzil bersin (bir xil so'rov ikkita `react-query` kalitiga bo'linmasin).
 */
export function parseAuditActions(raw: unknown): readonly AuditAction[] {
  if (raw === undefined || raw === null) return [];

  const values = new Set(
    (Array.isArray(raw) ? raw : [raw])
      .flatMap((item) => String(item).split(','))
      .map((item) => item.trim())
      .filter(isAuditAction),
  );

  return AUDIT_ACTIONS.filter((action) => values.has(action));
}

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ ⚠️ KALIT «TASHLAB YUBORILMAYDI» — U OSHKORA `undefined` QILIB QAYTADI     ║
 * ║                                                                          ║
 * ║ Bu faylning qolgan parserlari yaroqsiz qiymatni NATIJAGA QO'SHMAY        ║
 * ║ ketadi (`...(x !== undefined ? { x } : {})`). Niyat to'g'ri, natija esa   ║
 * ║ yo'q edi va buni JONLI o'lchash ko'rsatdi:                                ║
 * ║                                                                          ║
 * ║   /security?entityId=-5  →  GET /api/admin/audit?entityId=-5&limit=100   ║
 * ║   /invoices?page=-5      →  GET /api/invoices?page=-5&pageSize=25        ║
 * ║                                                                          ║
 * ║ Sabab: router `validateSearch` NATIJASINI xom qidiruv ustiga QO'YADI,    ║
 * ║ uning O'RNIGA qo'ymaydi. Ya'ni parser TEGMAGAN kalit URL dan o'z holicha ║
 * ║ o'tib ketadi. Tekshiruv esa faqat QAYTARILGAN kalitga ta'sir qiladi —    ║
 * ║ `?limit=99999` haqiqatan 500 ga tushadi va manzil ham qayta yoziladi,    ║
 * ║ chunki `limit` natijada BOR.                                             ║
 * ║                                                                          ║
 * ║ Oqibati aynan «ishonch bilan noto'g'ri javob»: `entityId=-5` serverdan   ║
 * ║ 400 oldiradi va ekran «So'rov parametrlari noto'g'ri» deb turadi —       ║
 * ║ sabab esa qo'lda tahrirlangan manzilda, ekranda emas.                    ║
 * ║                                                                          ║
 * ║ Shuning uchun bu yerda HAR BIR kalit QAYTARILADI: yaroqli qiymat yoki    ║
 * ║ `undefined`. `undefined` xom qiymatning ustiga yozilib uni o'chiradi.    ║
 * ║                                                                          ║
 * ║ TUZATISH FAQAT SHU PARSERDA. Bir xil kamchilik `parseListSearch` va      ║
 * ║ boshqa parserlarda ham bor (`/invoices?page=-5` — o'lchangan), lekin     ║
 * ║ ular o'nlab ekranga tegadi va bitta ekran ustida ishlayotganda ularni    ║
 * ║ tekshirmasdan o'zgartirish katta xavf. Bu — alohida ish.                 ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function parseAuditSearch(search: Record<string, unknown>): AuditSearch {
  const entityId = positiveInt(search.entityId);
  const actorId = positiveInt(search.actorId);
  const limit = positiveInt(search.limit);
  const from = text(search.from);
  const to = text(search.to);
  const action = parseAuditActions(search.action);
  const entityTable = text(search.entityTable);

  return {
    // Jadval nomining naqshi serverdagi bilan bir xil: kichik harf va `_`.
    entityTable: entityTable !== undefined && TABLE_NAME.test(entityTable) ? entityTable : undefined,
    // Kanonik satr: `?action=%5B%22update%22%5D` emas, `?action=update`.
    action: action.length > 0 ? action.join(',') : undefined,
    entityId,
    actorId,
    limit: limit !== undefined && limit !== 100 ? Math.min(limit, 500) : undefined,
    from: from !== undefined && ISO_DATE.test(from) ? from : undefined,
    to: to !== undefined && ISO_DATE.test(to) ? to : undefined,
  };
}

// ---------------------------------------------------------------------------
// Narx ro'yxatlari
// ---------------------------------------------------------------------------

export interface PriceListSearch {
  /** Ochilgan narx ro'yxati — satrlar shu bo'yicha yuklanadi. */
  readonly priceListId?: number;
}

export function parsePriceListSearch(search: Record<string, unknown>): PriceListSearch {
  const priceListId = positiveInt(search.priceListId);
  return priceListId === undefined ? {} : { priceListId };
}
